import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRoomSocket } from "@/hooks/useRoomSocket";
import { useAudioQueue } from "@/hooks/useAudioQueue";
import { useRecorder } from "@/hooks/useRecorder";
import { BootScreen } from "@/components/ProtectedRoute";
import { AuroraLogo } from "@/components/AuroraLogo";
import { RoomEntry } from "@/components/room/RoomEntry";
import { RoomHeader } from "@/components/room/RoomHeader";
import { MessageCard } from "@/components/room/MessageCard";
import { PttReactor, usePtt } from "@/components/room/PttReactor";
import { Participants } from "@/components/room/Participants";
import { Composer } from "@/components/room/Composer";
import { TranscriptButtons } from "@/components/room/TranscriptButtons";
import { useHandsFree } from "@/hooks/useHandsFree";
import { useLiveCaption } from "@/hooks/useLiveCaption";
import { LiveCaptions } from "@/components/room/LiveCaptions";
import { SensitivityControl, sensitivityToThreshold } from "@/components/room/SensitivityControl";
import { langLocale } from "@/lib/langs";
import { NoTraceBanner } from "@/components/room/Privacy";
import { warn } from "@/lib/log";

const HF_LABELS = { listening: "HANDS-FREE · JUST SPEAK", capturing: "CAPTURING YOUR VOICE", paused: "PAUSED WHILE AURORA SPEAKS", off: "STARTING MICROPHONE" };

const RoomNotice = ({ title, text, testId, children }) => (
  <div className="flex min-h-screen items-center justify-center px-4" data-testid={testId}>
    <div className="holo-panel corners w-full max-w-xl p-8 text-center sm:p-10">
      <div className="flex justify-center"><AuroraLogo size={64} /></div>
      <div className="eyebrow mt-8">SpeechFlow</div>
      <h1 className="mt-3 font-display text-2xl font-extrabold">{title}</h1>
      <p className="mt-3 text-sm text-muted-foreground">{text}</p>
      {children}
      <Link to="/dashboard" className="btn-primary mt-8" data-testid="room-back-dashboard">Back to dashboard</Link>
    </div>
  </div>
);

const ClosedNotice = ({ room }) => {
  const { user } = useAuth();
  const isParticipant = room.participants.some((p) => p.user_id === user.user_id);
  const [summary, setSummary] = useState(room.summary || "");
  useEffect(() => {
    if (summary || !isParticipant || room.encrypted) return undefined;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const { data } = await api.get(`/rooms/${room.room_id}`);
        if (data.summary) setSummary(data.summary);
      } catch (e) { warn("Summary poll failed, retrying", e); }
      if (n > 15) clearInterval(t);
    }, 3000);
    return () => clearInterval(t);
  }, [summary, isParticipant, room.room_id, room.encrypted]);
  return (
    <RoomNotice title="The call has ended" text="This SpeechFlow room is closed. Open a new room to start another call." testId="room-closed">
      {isParticipant && room.encrypted && (
        <div className="mt-8 border-t border-border pt-6 text-left" data-testid="room-closed-purged">
          <NoTraceBanner compact testId="room-closed-no-trace-banner" />
          <p className="mt-4 font-mono text-xs text-muted-foreground">&gt; All messages{room.purged_messages != null ? ` (${room.purged_messages})` : ""} and voice permanently deleted. Nothing was kept.</p>
        </div>
      )}
      {isParticipant && !room.encrypted && (
        <div className="mt-8 border-t border-border pt-6 text-left">
          <div className="eyebrow">Aurora summary</div>
          {summary ? (
            <p className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap border-l-2 border-[color:var(--au-violet)] pl-4 text-sm" data-testid="room-closed-summary">{summary}</p>
          ) : (
            <p className="caret mt-3 font-mono text-xs text-muted-foreground" data-testid="room-closed-summary-pending">&gt; Aurora is writing the summary</p>
          )}
          <p className="mt-4 font-mono text-[11px] text-muted-foreground">Transcript + summary are emailed to every participant.</p>
          <div className="mono-label mb-3 mt-6 text-center">Download transcript</div>
          <div className="flex justify-center"><TranscriptButtons roomId={room.room_id} /></div>
        </div>
      )}
    </RoomNotice>
  );
};

function RoomLive({ room, setRoom, onClosed }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const me = room.participants.find((p) => p.user_id === user.user_id);
  const [messages, setMessages] = useState([]);
  const [audioState, setAudioState] = useState({});
  const [online, setOnline] = useState(room.online || []);
  const [speaking, setSpeaking] = useState({});
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(0);
  const processing = pending > 0;
  const [handsFree, setHandsFree] = useState(false);
  const [sensitivity, setSensitivity] = useState(() => Number(localStorage.getItem("aurora_mic_sensitivity") || 70));
  const [captions, setCaptions] = useState({});
  useEffect(() => { localStorage.setItem("aurora_mic_sensitivity", String(sensitivity)); }, [sensitivity]);
  useEffect(() => {
    const t = setInterval(() => setCaptions((c) => {
      const now = Date.now();
      const kept = Object.fromEntries(Object.entries(c).filter(([, v]) => now - v.at < 12000));
      return Object.keys(kept).length === Object.keys(c).length ? c : kept;
    }), 3000);
    return () => clearInterval(t);
  }, []);
  const dropCaption = (uid) => setCaptions((c) => { if (!c[uid]) return c; const n = { ...c }; delete n[uid]; return n; });
  const endRef = useRef(null);
  const audio = useAudioQueue(room.room_id);
  const [failed, setFailed] = useState({});
  const syncMessages = useCallback(() => api.get(`/rooms/${room.room_id}/messages`).then(({ data }) => setMessages((prev) => data.map((m) => {
    const old = prev.find((p) => p.message_id === m.message_id);
    return old ? { ...m, translations: { ...old.translations, ...m.translations } } : m;
  }))).catch(() => {}), [room.room_id]);
  const closedRef = useRef(onClosed);
  closedRef.current = onClosed;
  const retryTranslation = async (m, lang) => {
    const key = `${m.message_id}:${lang}`;
    setFailed((f) => ({ ...f, [key]: "retrying" }));
    try {
      await api.post(`/rooms/${room.room_id}/messages/${m.message_id}/translate`, null, { params: { lang } });
      setFailed((f) => { const n = { ...f }; delete n[key]; return n; });
    } catch (e) {
      setFailed((f) => ({ ...f, [key]: "failed" }));
      toast.error(errMsg(e));
    }
  };

  const upsert = (m) => setMessages((list) => (list.some((x) => x.message_id === m.message_id) ? list : [...list, m]));

  const sock = useRoomSocket(room.room_id, {
    message: ({ message }) => {
      upsert(message);
      dropCaption(message.user_id);
    },
    open: () => syncMessages(),
    presence: (d) => setOnline(d.online),
    translations: (d) => setMessages((l) => l.map((m) => (m.message_id === d.message_id ? { ...m, translations: { ...m.translations, ...d.translations }, translate_ms: Math.max(m.translate_ms || 0, d.translate_ms || 0) } : m))),
    translation_error: (d) => setFailed((f) => ({ ...f, [`${d.message_id}:${d.language}`]: "failed" })),
    caption: (d) => setCaptions((c) => ({ ...c, [d.user_id]: { ...d, at: Date.now() } })),
    room: (d) => setRoom(d.room),
    speaking: (d) => setSpeaking((s) => ({ ...s, [d.user_id]: d.on })),
    room_closed: () => onClosed(),
  });


  useEffect(() => { syncMessages(); }, [syncMessages]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages.length]);

  const sendVoice = useCallback(async (blob, quiet = false) => {
    setPending((n) => n + 1);
    try {
      const fd = new FormData();
      fd.append("file", blob, `voice.${blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm"}`);
      const { data } = await api.post(`/rooms/${room.room_id}/voice`, fd);
      upsert(data);
    } catch (e) {
      if (!(quiet && [400, 422].includes(e.response?.status))) toast.error(errMsg(e));
    } finally {
      setPending((n) => n - 1);
    }
  }, [room.room_id]);

  const rec = useRecorder(sendVoice);
  const ptt = usePtt(rec, processing || handsFree);
  const hf = useHandsFree({ enabled: handsFree, paused: !!audio.playing, threshold: sensitivityToThreshold(sensitivity), onUtterance: (b) => sendVoice(b, true), onFail: () => setHandsFree(false) });
  const talking = rec.recording || hf.recording;
  const sendSpeaking = sock.send;
  useLiveCaption(langLocale(me?.language || "auto"), talking, (text) => sock.send({ type: "caption", text }));
  useEffect(() => { sendSpeaking({ type: "speaking", on: talking }); }, [talking, sendSpeaking]);
  const hfLabel = HF_LABELS[hf.state];
  const stopHandsFree = useMemo(() => ({ onPointerDown: (e) => { e.preventDefault(); setHandsFree(false); } }), []);

  const sendText = async (text, kind) => {
    setSending(true);
    try {
      const { data } = await api.post(`/rooms/${room.room_id}/messages`, { text, kind });
      upsert(data);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setSending(false);
    }
  };

  const endCall = async () => {
    try {
      await api.post(`/rooms/${room.room_id}/end`);
      onClosed();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    if (sock.status === "closed") closedRef.current();
  }, [sock.status]);

  const roomLangs = [...new Set(room.participants.map((p) => p.language))];
  const play = (m, lang) => {
    const key = `${m.message_id}:${lang}`;
    setAudioState((s) => ({ ...s, [key]: "loading" }));
    audio.playNow(m.message_id, lang).catch((e) => toast.error(errMsg(e) || "Voice unavailable, try again"))
      .finally(() => setAudioState((s) => ({ ...s, [key]: "ready" })));
  };

  return (
    <div className="flex h-screen flex-col" data-testid="room-live">
      <RoomHeader room={room} isHost={room.host_id === user.user_id} status={sock.status} rtt={sock.rtt}
        onEnd={endCall} onLeave={() => nav("/dashboard")} />
      <div className="mx-auto grid w-full max-w-[1440px] flex-1 grid-cols-1 overflow-hidden lg:grid-cols-12">
        <section className="flex min-h-0 flex-col border-border lg:col-span-8 lg:border-r">
          <div className="flex gap-2 overflow-x-auto border-b border-border px-4 py-2 lg:hidden" data-testid="room-participants-mobile">
            {room.participants.map((p) => (
              <span key={p.user_id} className={`shrink-0 border px-2 py-1 font-mono text-[10px] ${speaking[p.user_id] ? "border-[color:var(--au-green)] text-[color:var(--au-green)]" : "border-border"}`}>
                {online.includes(p.user_id) ? "●" : "○"} {p.name} [{p.language.toUpperCase()}]
              </span>
            ))}
          </div>
          {room.encrypted && <NoTraceBanner compact testId="room-live-no-trace-banner" />}
          <div className="flex-1 space-y-6 overflow-y-auto px-4 py-6 sm:px-8" data-testid="room-feed">
            {messages.length === 0 && (
              <div className="mx-auto max-w-md py-16 text-center">
                <div className="eyebrow">Channel open</div>
                <p className="caret mt-4 font-mono text-sm text-muted-foreground">&gt; Speak or type in {me?.language === "auto" ? "any language" : `[${me?.language?.toUpperCase()}]`}. Everyone reads it in their own language. Invite up to 3 guests.</p>
              </div>
            )}
            {messages.map((m) => (
              <MessageCard key={m.message_id} m={m} mine={m.user_id === user.user_id} myLang={me?.language} langs={roomLangs} failed={failed} onRetry={(lang) => retryTranslation(m, lang)}
                audioState={audioState} playing={audio.playing} onPlay={(lang) => play(m, lang)} />
            ))}
            <div ref={endRef} />
          </div>
          <LiveCaptions captions={captions} me={user.user_id} />
          {handsFree && <SensitivityControl value={sensitivity} onChange={setSensitivity} level={hf.level} variant="mobile" className="border-t border-border px-4 py-3 lg:hidden" />}
          <Composer myLang={me?.language || "auto"} onSend={sendText} sending={sending} rec={rec} pttHandlers={ptt} processing={processing}
            handsFree={handsFree} hf={hf} onToggleHandsFree={() => setHandsFree((v) => !v)} />
        </section>
        <aside className="hidden min-h-0 flex-col overflow-y-auto lg:col-span-4 lg:flex">
          <div className="mx-auto mt-6 grid w-64 grid-cols-2 border border-border" data-testid="room-voice-mode-switch">
            {[[false, "Push-to-talk"], [true, "Hands-free"]].map(([v, l]) => (
              <button key={l} onClick={() => setHandsFree(v)} data-testid={`room-mode-${v ? "handsfree" : "ptt"}`}
                className={`h-9 font-mono text-[10px] uppercase tracking-widest transition-colors duration-200 ${handsFree === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          {handsFree ? (
            <>
            <PttReactor rec={hf} processing={false} label={processing ? `${hfLabel} · TRANSLATING` : hfLabel}
              handlers={stopHandsFree} />
            <SensitivityControl value={sensitivity} onChange={setSensitivity} level={hf.level} className="mx-auto mb-6 w-72" />
            </>
          ) : (
            <PttReactor rec={rec} processing={processing} handlers={ptt} />
          )}
          <div className="border-t border-border"><Participants room={room} online={online} speaking={speaking} me={user.user_id} /></div>
        </aside>
      </div>
    </div>
  );
}

export default function Room() {
  const { roomId } = useParams();
  const loc = useLocation();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(!!loc.state?.joined);

  useEffect(() => {
    api.get(`/rooms/${roomId}`).then(({ data }) => setRoom(data)).catch((e) => setError(errMsg(e)));
  }, [roomId]);

  if (error) return <RoomNotice title="Room not found" text={error} testId="room-not-found" />;
  if (!room) return <BootScreen label="LOCATING SPEECHFLOW ROOM" />;
  if (room.status === "closed") return <ClosedNotice room={room} />;
  if (!joined) return <RoomEntry room={room} onJoined={(r) => { setRoom(r); setJoined(true); }} />;
  const closeRoom = () => {
    setRoom((r) => ({ ...r, status: "closed" }));
    api.get(`/rooms/${roomId}`).then(({ data }) => setRoom(data)).catch(() => {});
  };
  return <RoomLive room={room} setRoom={setRoom} onClosed={closeRoom} />;
}
